<template>
    <div style="width: 100vw;height: 100vh">
	    <div id="threeContainer"></div>
    </div>
</template>

<script>
import {
    AxesHelper,
    Mesh,
    MeshBasicMaterial,
    PerspectiveCamera,
    Quaternion,
    Scene,
    SphereGeometry,
    Spherical,
    TextureLoader,
    Vector2,
    Vector3,
    WebGLRenderer
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { Easing, remove, Tween, update } from '@tweenjs/tween.js'

let camera;
let renderer;
let scene;
let controls;

function init() {
    const container = document.getElementById('threeContainer');
    // const axes = new AxesHelper(200);
    camera = new PerspectiveCamera(100, window.innerWidth / window.innerHeight, 0.1, 10000);
    camera.position.set(0, 1700, 0);

    const geometryOutside = new SphereGeometry(1600, 50, 50);
    geometryOutside.scale(-1, 1, 1);
    const geometryInside = new SphereGeometry(500, 100, 100);
    geometryInside.scale(-1, 1, 1);

    const textureInside = new TextureLoader().load('/image/panorama.jpg');
    const materialInside = new MeshBasicMaterial({ map: textureInside });

    const textureOutside = new TextureLoader().load('/image/panorama.jpg');
    const materialOutside = new MeshBasicMaterial({ map: textureOutside });
    scene = new Scene();
    // scene.add(axes);
    scene.add(new Mesh(geometryOutside, materialOutside), new Mesh(geometryInside, materialInside));

    renderer = new WebGLRenderer();
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(window.innerWidth, window.innerHeight);
    container.appendChild(renderer.domElement);

    controls = new OrbitControls(camera, renderer.domElement);
    controls.enablePan = false;
    controls.rotateSpeed = - 0.25;

    let tween = new Tween({ fov: camera.fov, zoom: camera.zoom })
        .to({ fov: 70, zoom: 5 }, 2000)
        .delay(2000)
        .easing(Easing.Linear.None)
        .onComplete(() => {
            remove(tween);
            controls.maxDistance = 1820;
        })
        .onUpdate((t) => {
            camera.fov = t.fov;
            camera.zoom = t.zoom;
            camera.updateProjectionMatrix();
        })
        .start();

    window.addEventListener('resize', onWindowResize);
}

function onWindowResize() {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
}

function animate() {
    requestAnimationFrame(animate),
    update(),
    controls.update(),
    renderer.render(scene, camera);
}

export default {
    name: 'panorama',

    props: {
        downEvent: {
            clientX: Number,
            clientY: Number,
        },
        moveEvent: {
            clientX: Number,
            clientY: Number,
        },
        wheelEvent: { deltaY: Number },
    },

    components: {},

    data() {
        return {
            scale: 1,
            rotateStart: new Vector2(),
            rotateEnd: new Vector2(),
            rotateDelta: new Vector2(),
	        spherical: new Spherical(),
	        sphericalDelta: new Spherical(),
        }
    },

    mounted() {
        init();
        animate();
    },

    watch: {
        downEvent(newValue) {
            if (this.downEvent) {
                this.rotateStart.set(newValue.clientX, newValue.clientY);
            }
        },
        moveEvent(newValue) {
            if (this.moveEvent) {
                this.rotateEnd.set(newValue.clientX, newValue.clientY);
                this.rotateDelta.subVectors(this.rotateEnd, this.rotateStart).multiplyScalar(controls.rotateSpeed);
                this.rotateLeft(2 * Math.PI * this.rotateDelta.x / renderer.domElement.clientHeight);
                this.rotateUp(2 * Math.PI * this.rotateDelta.y / renderer.domElement.clientHeight);
                this.rotateStart.copy(this.rotateEnd);
                this.update();
            }
        },
        wheelEvent(newValue) {
            if (this.wheelEvent) {
                if (newValue.deltaY < 0) this.dollyIn(this.getZoomScale());
                else if (newValue.deltaY > 0) this.dollyOut(this.getZoomScale());
                this.update();
            }
        },
    },

    methods: {
        rotateLeft(angle) {
            this.sphericalDelta.theta -= angle;
        },
        rotateUp(angle) {
            this.sphericalDelta.phi -= angle;
        },
        dollyIn(dollyScale) {
            this.scale *= dollyScale;
        },
        dollyOut(dollyScale) {
            this.scale /= dollyScale;
        },
        getZoomScale() {
            return Math.pow(0.95, controls.zoomSpeed);
        },

        update() {
            let offset = new Vector3();
            const quat = new Quaternion().setFromUnitVectors(camera.up, new Vector3(0, 1, 0));
            const quatInverse = quat.clone().invert();
            const lastPosition = new Vector3();
            const lastQuaternion = new Quaternion();
            const EPS = 0.000001;
            const position = camera.position;
            offset.copy(position).sub(controls.target);
			offset.applyQuaternion(quat);
			this.spherical.setFromVector3(offset);
            this.spherical.theta += this.sphericalDelta.theta;
            this.spherical.phi += this.sphericalDelta.phi;
			this.spherical.theta = Math.max(controls.minAzimuthAngle, Math.min(controls.maxAzimuthAngle, this.spherical.theta));
			this.spherical.phi = Math.max(controls.minPolarAngle, Math.min(controls.maxPolarAngle, this.spherical.phi));
            this.spherical.makeSafe();
			this.spherical.radius *= this.scale;
			this.spherical.radius = Math.max(controls.minDistance, Math.min(controls.maxDistance, this.spherical.radius));
            offset.setFromSpherical(this.spherical);
            offset.applyQuaternion(quatInverse);
            position.copy(controls.target).add(offset);
            camera.lookAt(controls.target);
            this.sphericalDelta.set(0, 0, 0);
            this.scale = 1;
            if (lastPosition.distanceToSquared(camera.position) > EPS || 8 * (1 - lastQuaternion.dot(camera.quaternion)) > EPS) {
                // controls.dispatchEvent({ type: 'change' });
                lastPosition.copy(camera.position);
                lastQuaternion.copy(camera.quaternion);
            }
        },
    }
}
</script>

<style lang="less" scoped>
canvas {
    width: 100vw;
    height: 100vh;
}
</style>
